import React, { useState, useRef, useEffect } from 'react';

const SwipeableItem = ({ children, onDelete, threshold = 80 }) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDistance, setSwipeDistance] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const itemRef = useRef(null);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setStartX(touch.clientX);
    setIsSwiping(true);
    setSwipeDistance(0);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const distance = startX - currentX;
    
    if (distance > 0) {
      // Only prevent default if we're actually swiping
      if (distance > 10) {
        e.preventDefault();
      }
      setSwipeDistance(Math.min(distance, threshold * 1.5));
    }
  };

  const handleTouchEnd = (e) => {
    if (!isSwiping) return;
    
    setIsSwiping(false);
    
    if (swipeDistance >= threshold) {
      // Continue sliding off to the left
      setIsDeleting(true);
      setTimeout(() => {
        onDelete();
      }, 300);
    } else {
      // Snap back to original position
      setSwipeDistance(0);
    }
  };

  const handleMouseDown = (e) => {
    // Only start swiping if it's a left mouse button and not on interactive elements
    if (e.button !== 0) return;
    
    const target = e.target;
    if (target.closest('button') || target.closest('input') || target.closest('a')) {
      return;
    }
    
    setStartX(e.clientX);
    setIsSwiping(true);
    setSwipeDistance(0);
  };

  const handleMouseMove = (e) => {
    if (!isSwiping) return;
    
    const currentX = e.clientX;
    const distance = startX - currentX;
    
    if (distance > 0) {
      setSwipeDistance(Math.min(distance, threshold * 1.5));
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSwiping) {
        setIsSwiping(false);
        if (swipeDistance < threshold) {
          // Snap back to original position
          setSwipeDistance(0);
        } else {
          // Continue sliding off to the left
          setIsDeleting(true);
          setTimeout(() => {
            onDelete();
          }, 300);
        }
      }
    };

    const handleGlobalMouseMove = (e) => {
      if (isSwiping) {
        handleMouseMove(e);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('mousemove', handleGlobalMouseMove);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isSwiping, swipeDistance, threshold, onDelete]);

  const swipePercentage = Math.min((swipeDistance / threshold) * 100, 100);
  const shouldShowDelete = swipeDistance >= threshold * 0.5;

  return (
    <div 
      ref={itemRef}
      className={`swipeable-item ${isDeleting ? 'deleting' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
    >
      {/* Delete background - stays in place */}
      <div 
        className={`swipe-delete-background ${shouldShowDelete ? 'visible' : ''}`}
        style={{
          opacity: swipePercentage / 100
        }}
      >
        <span className="swipe-delete-text">🗑️ Remove</span>
      </div>
      
      {/* Item content - moves over the background */}
      <div 
        className="swipeable-item-content"
        style={{
          transform: isDeleting 
            ? 'translateX(-100%)' 
            : `translateX(-${swipeDistance}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableItem; 